import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box, Button, Paper, Stack, TextField, Typography, Grid,
  FormControlLabel, Checkbox, Divider, IconButton, Alert, Chip, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableBody, TableCell, TableHead, TableRow,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import PeopleIcon from "@mui/icons-material/People";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Swal from "sweetalert2";
import CampoMoeda from "../../components/CampoMoeda";
import {
  obterEvento, salvarEvento, uploadBanner, bannerUrl, listarInscritosEvento,
} from "../../services/eventoService";

const vazio = {
  Id: 0, Nome: "", Descricao: "", Local: "", DataInicio: "", DataFim: "",
  InicioInscricao: "", FimInscricao: "", Pago: false, Vagas: "", Visivel: true, Ativo: true, Valores: [],
  CobrarPorFilho: false, ValorFilho: 0, IdadeMinimaCobranca: 12, Grupos: [], Encontros: [],
};
const novoValor = () => ({ Id: 0, Nome: "", Valor: 0, DataInicial: "", DataFinal: "", Vagas: "", Ativo: true });
const novoGrupo = () => ({ Id: 0, Nome: "", Obrigatorio: true, Opcoes: [{ Id: 0, Nome: "", Vagas: "", Ativo: true }] });
const novaOpcao = () => ({ Id: 0, Nome: "", Vagas: "", Ativo: true });
const novoEncontro = () => ({ Id: 0, Nome: "", Data: "" });
const dtLocal = (d) => (d ? new Date(d).toISOString().slice(0, 16) : "");
const fmt = (d) => (d ? new Date(d).toLocaleString("pt-BR") : "—");
const moeda = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export default function EventoForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [e, setE] = useState(vazio);
  const [temBanner, setTemBanner] = useState(false);
  const [verInscritos, setVerInscritos] = useState(false);
  const [inscritos, setInscritos] = useState([]);
  const [cacheBuster, setCacheBuster] = useState(0);
  const set = (k, v) => setE((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
      const { data } = await obterEvento(id);
      setE({
        Id: data.Id, Nome: data.Nome || "", Descricao: data.Descricao || "", Local: data.Local || "",
        DataInicio: dtLocal(data.DataInicio), DataFim: dtLocal(data.DataFim),
        InicioInscricao: data.InicioInscricao || "", FimInscricao: data.FimInscricao || "",
        Pago: !!data.Pago, Vagas: data.Vagas ?? "", Visivel: !!data.Visivel, Ativo: !!data.Ativo,
        CobrarPorFilho: !!data.CobrarPorFilho, ValorFilho: data.ValorFilho ?? 0, IdadeMinimaCobranca: data.IdadeMinimaCobranca ?? 12,
        Grupos: (data.Grupos || []).map((g) => ({
          Id: g.Id, Nome: g.Nome, Obrigatorio: !!g.Obrigatorio,
          Opcoes: (g.Opcoes || []).map((o) => ({ Id: o.Id, Nome: o.Nome, Vagas: o.Vagas ?? "", Ativo: !!o.Ativo })),
        })),
        Encontros: (data.Encontros || []).map((e) => ({ Id: e.Id, Nome: e.Nome, Data: e.Data || "" })),
        Valores: (data.Valores || []).map((v) => ({
          Id: v.Id, Nome: v.Nome, Valor: v.Valor, DataInicial: v.DataInicial || "",
          DataFinal: v.DataFinal || "", Vagas: v.Vagas ?? "", Ativo: v.Ativo,
        })),
      });
      setTemBanner(!!data.Banner);
      } catch (err) {
        Swal.fire("Erro", err?.response?.data?.mensagem || err?.response?.data || err.message, "error");
      }
    })();
  }, [id]);

  const updValor = (i, k, v) => set("Valores", e.Valores.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const addValor = () => set("Valores", [...e.Valores, novoValor()]);
  const delValor = (i) => set("Valores", e.Valores.filter((_, idx) => idx !== i));

  // Grupos de opção (oficinas)
  const updGrupo = (gi, k, v) => set("Grupos", e.Grupos.map((g, idx) => (idx === gi ? { ...g, [k]: v } : g)));
  const addGrupo = () => set("Grupos", [...e.Grupos, novoGrupo()]);
  const delGrupo = (gi) => set("Grupos", e.Grupos.filter((_, idx) => idx !== gi));
  const addOpcao = (gi) => set("Grupos", e.Grupos.map((g, idx) => (idx === gi ? { ...g, Opcoes: [...g.Opcoes, novaOpcao()] } : g)));
  const delOpcao = (gi, oi) => set("Grupos", e.Grupos.map((g, idx) => (idx === gi ? { ...g, Opcoes: g.Opcoes.filter((_, j) => j !== oi) } : g)));
  const updOpcao = (gi, oi, k, v) => set("Grupos", e.Grupos.map((g, idx) => (idx === gi ? { ...g, Opcoes: g.Opcoes.map((o, j) => (j === oi ? { ...o, [k]: v } : o)) } : g)));

  // Encontros (presença)
  const updEncontro = (i, k, v) => set("Encontros", e.Encontros.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const addEncontro = () => set("Encontros", [...e.Encontros, novoEncontro()]);
  const delEncontro = (i) => set("Encontros", e.Encontros.filter((_, idx) => idx !== i));

  const salvar = async () => {
    if (!e.Nome.trim()) return Swal.fire("Atenção", "Informe o nome do evento.", "warning");
    if (e.Pago && !e.Valores.some((v) => v.Ativo)) return Swal.fire("Atenção", "Evento pago precisa de ao menos um valor ativo.", "warning");
    if (e.CobrarPorFilho && !(Number(e.ValorFilho) > 0)) return Swal.fire("Atenção", "Informe o valor adicional por filho/aluno.", "warning");
    try {
      const dto = {
        ...e,
        Vagas: e.Vagas === "" ? null : Number(e.Vagas),
        ValorFilho: Number(e.ValorFilho || 0),
        IdadeMinimaCobranca: Number(e.IdadeMinimaCobranca || 12),
        DataInicio: e.DataInicio || null, DataFim: e.DataFim || null,
        InicioInscricao: e.InicioInscricao || null, FimInscricao: e.FimInscricao || null,
        Valores: e.Valores.map((v, idx) => ({
          ...v, Valor: Number(v.Valor || 0), Ordem: idx,
          DataInicial: v.DataInicial || null, DataFinal: v.DataFinal || null,
          Vagas: v.Vagas === "" ? null : Number(v.Vagas),
        })),
        Grupos: e.Grupos.map((g, gi) => ({
          Id: g.Id, Nome: g.Nome, Obrigatorio: !!g.Obrigatorio, Ordem: gi,
          Opcoes: g.Opcoes.map((o, oi) => ({
            Id: o.Id, Nome: o.Nome, Ativo: o.Ativo !== false, Ordem: oi,
            Vagas: o.Vagas === "" || o.Vagas == null ? null : Number(o.Vagas),
          })),
        })),
        Encontros: e.Encontros.map((en, i) => ({ Id: en.Id, Nome: en.Nome, Data: en.Data || null, Ordem: i })),
      };
      const { data } = await salvarEvento(dto);
      await Swal.fire("Sucesso", data.mensagem, "success");
      if (!id) navigate(`/eventos-admin/${data.id}`); else { set("Id", data.id); }
    } catch (err) { Swal.fire("Erro", err?.response?.data || err.message, "error"); }
  };

  const enviarBanner = async (file) => {
    if (!file) return;
    try {
      await uploadBanner(e.Id, file);
      setTemBanner(true); setCacheBuster(Date.now());
      Swal.fire("Pronto", "Banner atualizado.", "success");
    } catch (err) { Swal.fire("Erro", err?.response?.data || err.message, "error"); }
  };

  const abrirInscritos = async () => {
    try { setInscritos((await listarInscritosEvento(e.Id)).data); setVerInscritos(true); }
    catch (err) { Swal.fire("Erro", err?.response?.data || err.message, "error"); }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" mb={2}>
        <Typography variant="h5">{id ? `Evento #${id}` : "Novo evento"}</Typography>
        <Stack direction="row" spacing={1}>
          <Button onClick={() => navigate("/eventos-admin")}>Voltar</Button>
          {e.Id > 0 && <Button variant="outlined" startIcon={<PeopleIcon />} onClick={abrirInscritos}>Inscritos</Button>}
          <Button variant="contained" onClick={salvar}>Salvar</Button>
        </Stack>
      </Stack>

      {e.Id > 0 && (
        <Alert
          severity={e.Visivel && e.Ativo ? "success" : "warning"} sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" startIcon={<ContentCopyIcon />}
              onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/eventos/${e.Id}`); Swal.fire({ toast: true, position: "top-end", icon: "success", title: "Link copiado", showConfirmButton: false, timer: 1500 }); }}>
              Copiar link
            </Button>
          }
        >
          {e.Visivel && e.Ativo
            ? <>Link de inscrição: <code style={{ wordBreak: "break-all" }}>{`${window.location.origin}/eventos/${e.Id}`}</code></>
            : "Marque o evento como Publicado e Ativo (e salve) para liberar o link de inscrição."}
          {" "}As inscrições aparecem no botão <b>Inscritos</b> acima.
        </Alert>
      )}

      <Paper sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 8 }}><TextField fullWidth label="Nome do evento" value={e.Nome} onChange={(ev) => set("Nome", ev.target.value)} /></Grid>
          <Grid size={{ xs: 12, sm: 4 }}><TextField fullWidth label="Local" value={e.Local} onChange={(ev) => set("Local", ev.target.value)} /></Grid>
          <Grid size={{ xs: 12 }}><TextField fullWidth multiline minRows={2} label="Descrição" value={e.Descricao} onChange={(ev) => set("Descricao", ev.target.value)} /></Grid>

          <Grid size={{ xs: 6, sm: 3 }}><TextField fullWidth type="datetime-local" label="Início do evento" InputLabelProps={{ shrink: true }} value={e.DataInicio} onChange={(ev) => set("DataInicio", ev.target.value)} /></Grid>
          <Grid size={{ xs: 6, sm: 3 }}><TextField fullWidth type="datetime-local" label="Fim do evento" InputLabelProps={{ shrink: true }} value={e.DataFim} onChange={(ev) => set("DataFim", ev.target.value)} /></Grid>
          <Grid size={{ xs: 6, sm: 3 }}><TextField fullWidth type="date" label="Início inscrições" InputLabelProps={{ shrink: true }} value={e.InicioInscricao} onChange={(ev) => set("InicioInscricao", ev.target.value)} /></Grid>
          <Grid size={{ xs: 6, sm: 3 }}><TextField fullWidth type="date" label="Fim inscrições" InputLabelProps={{ shrink: true }} value={e.FimInscricao} onChange={(ev) => set("FimInscricao", ev.target.value)} /></Grid>

          <Grid size={{ xs: 6, sm: 3 }}><TextField fullWidth type="number" label="Vagas (vazio = ilimitado)" value={e.Vagas} onChange={(ev) => set("Vagas", ev.target.value)} /></Grid>
          <Grid size={{ xs: 12, sm: 9 }} sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
            <FormControlLabel control={<Checkbox checked={e.Pago} onChange={(ev) => set("Pago", ev.target.checked)} />} label="Evento pago (com taxa)" />
            <FormControlLabel control={<Checkbox checked={e.Visivel} onChange={(ev) => set("Visivel", ev.target.checked)} />} label="Publicado (visível ao público)" />
            <FormControlLabel control={<Checkbox checked={e.Ativo} onChange={(ev) => set("Ativo", ev.target.checked)} />} label="Ativo" />
          </Grid>

          {/* Cobrança por filho/aluno */}
          <Grid size={{ xs: 12 }}>
            <Typography variant="subtitle2" color="primary" sx={{ mt: 1 }}>Cobrança por filho/aluno</Typography>
            <Divider sx={{ mt: 0.5, mb: 1 }} />
            <FormControlLabel control={<Checkbox checked={e.CobrarPorFilho} onChange={(ev) => set("CobrarPorFilho", ev.target.checked)} />}
              label="Cobrar adicional pela idade do filho/aluno selecionado" />
          </Grid>
          {e.CobrarPorFilho && (
            <>
              <Grid size={{ xs: 12 }}>
                <Alert severity="info">
                  O responsável escolhe um ou mais filhos. Cada aluno com a idade mínima ou mais soma o
                  <b> valor adicional</b> abaixo. O <b>total</b> = valor da inscrição (categoria acima, se o evento for pago)
                  <b> + </b> adicional de cada filho elegível — tudo num único PIX.
                  Requer a <b>busca de matrícula</b> configurada em Configurações → PIX (ERP).
                </Alert>
              </Grid>
              <Grid size={{ xs: 6, sm: 4 }}><CampoMoeda label="Valor adicional por filho/aluno" value={e.ValorFilho} onChange={(n) => set("ValorFilho", n)} /></Grid>
              <Grid size={{ xs: 6, sm: 3 }}><TextField fullWidth type="number" label="Idade mínima (anos)" value={e.IdadeMinimaCobranca} onChange={(ev) => set("IdadeMinimaCobranca", ev.target.value)} /></Grid>
            </>
          )}

          {/* Banner */}
          <Grid size={{ xs: 12 }}>
            <Typography variant="subtitle2" color="primary" sx={{ mt: 1 }}>Banner de divulgação</Typography>
            <Divider sx={{ mt: 0.5, mb: 1 }} />
            {e.Id === 0 ? (
              <Alert severity="info">Salve o evento para habilitar o envio do banner.</Alert>
            ) : (
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                {temBanner && (
                  <Box component="img" src={`${bannerUrl(e.Id)}?v=${cacheBuster}`} alt="banner"
                    sx={{ maxWidth: 320, maxHeight: 140, objectFit: "contain", border: "1px solid #ddd", borderRadius: 1 }} />
                )}
                <Button component="label" variant="outlined" startIcon={<UploadFileIcon />}>
                  {temBanner ? "Trocar banner" : "Enviar banner"}
                  <input type="file" hidden accept="image/*" onChange={(ev) => { enviarBanner(ev.target.files?.[0]); ev.target.value = ""; }} />
                </Button>
              </Stack>
            )}
          </Grid>

          {/* Valores */}
          {e.Pago && (
            <Grid size={{ xs: 12 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
                <Typography variant="subtitle2" color="primary">Valores / categorias / lotes (valor da inscrição)</Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={addValor}>Adicionar</Button>
              </Stack>
              <Divider sx={{ mt: 0.5, mb: 1 }} />
              {e.Valores.length === 0 && <Typography variant="body2" color="text.secondary">Nenhum valor cadastrado.</Typography>}
              {e.Valores.map((v, i) => (
                <Stack key={i} direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
                  <TextField size="small" label="Nome (ex.: Estudante - Lote 1)" sx={{ flex: 2, minWidth: 220 }} value={v.Nome} onChange={(ev) => updValor(i, "Nome", ev.target.value)} />
                  <CampoMoeda label="Valor" sx={{ width: 130 }} value={v.Valor} onChange={(n) => updValor(i, "Valor", n)} />
                  <TextField size="small" type="date" label="De" InputLabelProps={{ shrink: true }} value={v.DataInicial} onChange={(ev) => updValor(i, "DataInicial", ev.target.value)} />
                  <TextField size="small" type="date" label="Até" InputLabelProps={{ shrink: true }} value={v.DataFinal} onChange={(ev) => updValor(i, "DataFinal", ev.target.value)} />
                  <TextField size="small" type="number" label="Vagas" sx={{ width: 90 }} value={v.Vagas} onChange={(ev) => updValor(i, "Vagas", ev.target.value)} />
                  <FormControlLabel control={<Checkbox checked={v.Ativo} onChange={(ev) => updValor(i, "Ativo", ev.target.checked)} />} label="Ativo" />
                  <IconButton color="error" onClick={() => delValor(i)}><DeleteIcon /></IconButton>
                </Stack>
              ))}
            </Grid>
          )}

          {/* Oficinas / grupos de escolha */}
          <Grid size={{ xs: 12 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
              <Typography variant="subtitle2" color="primary">Oficinas / opções de escolha</Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={addGrupo}>Adicionar grupo</Button>
            </Stack>
            <Divider sx={{ mt: 0.5, mb: 1 }} />
            <Typography variant="caption" color="text.secondary">
              Cada grupo é uma escolha na inscrição (ex.: "Oficina do dia 18/08"), e o participante escolhe uma das opções. Defina as vagas por opção.
            </Typography>
            {e.Grupos.length === 0 && <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Nenhum grupo de escolha (opcional).</Typography>}
            {e.Grupos.map((g, gi) => (
              <Paper key={gi} variant="outlined" sx={{ p: 1.5, mt: 1.5 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <TextField size="small" label="Nome do grupo (ex.: Oficina do dia 18/08)" sx={{ flex: 2, minWidth: 260 }} value={g.Nome} onChange={(ev) => updGrupo(gi, "Nome", ev.target.value)} />
                  <FormControlLabel control={<Checkbox checked={g.Obrigatorio} onChange={(ev) => updGrupo(gi, "Obrigatorio", ev.target.checked)} />} label="Obrigatório" />
                  <Box sx={{ flexGrow: 1 }} />
                  <Button size="small" startIcon={<AddIcon />} onClick={() => addOpcao(gi)}>Opção</Button>
                  <IconButton color="error" onClick={() => delGrupo(gi)}><DeleteIcon /></IconButton>
                </Stack>
                <Divider sx={{ my: 1 }} />
                {g.Opcoes.map((o, oi) => (
                  <Stack key={oi} direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }} flexWrap="wrap" useFlexGap>
                    <TextField size="small" label="Opção (ex.: Oficina 1)" sx={{ flex: 2, minWidth: 240 }} value={o.Nome} onChange={(ev) => updOpcao(gi, oi, "Nome", ev.target.value)} />
                    <TextField size="small" type="number" label="Vagas" sx={{ width: 100 }} value={o.Vagas} onChange={(ev) => updOpcao(gi, oi, "Vagas", ev.target.value)} placeholder="ilimit." />
                    <FormControlLabel control={<Checkbox checked={o.Ativo !== false} onChange={(ev) => updOpcao(gi, oi, "Ativo", ev.target.checked)} />} label="Ativa" />
                    <IconButton size="small" color="error" onClick={() => delOpcao(gi, oi)}><DeleteIcon fontSize="small" /></IconButton>
                  </Stack>
                ))}
              </Paper>
            ))}
          </Grid>

          {/* Encontros (presença) */}
          <Grid size={{ xs: 12 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
              <Typography variant="subtitle2" color="primary">Encontros (registro de presença)</Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={addEncontro}>Adicionar encontro</Button>
            </Stack>
            <Divider sx={{ mt: 0.5, mb: 1 }} />
            <Typography variant="caption" color="text.secondary">
              Cada encontro é um dia com controle de presença (ex.: "12/08 – Palestra"). Aparecem na tela de Presença.
            </Typography>
            {e.Encontros.length === 0 && <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Nenhum encontro (opcional).</Typography>}
            {e.Encontros.map((en, i) => (
              <Stack key={i} direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                <TextField size="small" label="Nome (ex.: 12/08 – Palestra)" sx={{ flex: 2, minWidth: 260 }} value={en.Nome} onChange={(ev) => updEncontro(i, "Nome", ev.target.value)} />
                <TextField size="small" type="date" label="Data" InputLabelProps={{ shrink: true }} value={en.Data} onChange={(ev) => updEncontro(i, "Data", ev.target.value)} />
                <IconButton color="error" onClick={() => delEncontro(i)}><DeleteIcon /></IconButton>
              </Stack>
            ))}
          </Grid>
        </Grid>
      </Paper>

      <Dialog open={verInscritos} onClose={() => setVerInscritos(false)} maxWidth="md" fullWidth>
        <DialogTitle>Inscritos — {e.Nome}</DialogTitle>
        <DialogContent dividers>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Candidato</TableCell><TableCell>CPF</TableCell><TableCell>Aluno / Matrícula</TableCell>
                <TableCell>Turma</TableCell><TableCell>Categoria</TableCell>
                <TableCell>Valor</TableCell><TableCell>Situação</TableCell><TableCell>Inscrito em</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {inscritos.map((i) => (
                <TableRow key={i.Id}>
                  <TableCell>{i.Candidato}</TableCell>
                  <TableCell>{i.Cpf}</TableCell>
                  <TableCell>{i.AlunoNome || "—"}{i.Matricula ? ` (${i.Matricula})` : ""}</TableCell>
                  <TableCell>{i.Turma || "—"}</TableCell>
                  <TableCell>{i.Categoria || "—"}</TableCell>
                  <TableCell>{i.ValorDevido != null ? moeda(i.ValorDevido) : "—"}</TableCell>
                  <TableCell>
                    {i.Status === "X" ? <Chip size="small" label="Cancelada" />
                      : i.Pago ? <Chip size="small" color="success" label="Pago" />
                      : i.Status === "C" ? <Chip size="small" color="success" label="Confirmada" />
                      : <Chip size="small" color="warning" label="Aguardando pagamento" />}
                    {i.PixErro && (
                      <Tooltip title={i.PixErro}><Chip size="small" color="error" variant="outlined" label="Erro PIX" sx={{ ml: 0.5 }} /></Tooltip>
                    )}
                  </TableCell>
                  <TableCell>{fmt(i.DtCadastro)}</TableCell>
                </TableRow>
              ))}
              {inscritos.length === 0 && <TableRow><TableCell colSpan={8} align="center">Nenhum inscrito.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions><Button onClick={() => setVerInscritos(false)}>Fechar</Button></DialogActions>
      </Dialog>
    </Box>
  );
}
